import { Button, message } from 'antd';
import i18next from 'i18next';
import React, { useRef, useState } from 'react';
import { downloadPlanchePdf } from './exportPlanche';
import { DTFItem, Planche } from './packing';
import { DTFSettings } from './settings';

interface IProps {
	planche: Planche;
	index: number;
	/** boutons de téléchargement (admins du site seulement) */
	canDownload?: boolean;
	/** admin : PDF composé côté serveur (bibliothèque, 15.10.2026) ; absent = ancien export navigateur (mode test localhost) */
	onPdfServer?: (index: number) => Promise<void>;
	items: DTFItem[];
	settings: DTFSettings;
	pxPerCm: number;
	/** Damier sombre pour vérifier les logos/textes blancs (le film DTF est transparent) */
	dark?: boolean;
	/** Outil mesure actif : tirer un trait sur la planche → distance en cm */
	measure?: boolean;
	selectedId?: string | null;
	hoverId?: string | null;
	onSelect?: (id: string | null) => void;
	/** contenu rendu au centre de la planche vide (invite de dépôt) */
	invite?: React.ReactNode;
}

const RULER = 22;

/** Trait de mesure : cliquer-glisser sur la planche, distance affichée en cm.
 * Clic simple (sans glisser) ou Échap → efface le trait.
 * Le trait est stocké en CM (pas en px écran) : il reste juste après un zoom. */
const MeasureOverlay: React.FC<{ pxPerCm: number }> = ({ pxPerCm }) => {
	const [line, setLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
	const dragging = useRef(false);
	React.useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setLine(null);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);
	const pos = (e: React.PointerEvent<HTMLDivElement>) => {
		const r = e.currentTarget.getBoundingClientRect();
		return { x: (e.clientX - r.left) / pxPerCm, y: (e.clientY - r.top) / pxPerCm };
	};
	const cm = line ? Math.hypot(line.x2 - line.x1, line.y2 - line.y1) : 0;
	return (
		<div
			className="dtf-measure-overlay"
			onClick={e => e.stopPropagation()}
			onPointerDown={e => {
				e.stopPropagation();
				// sans ça, un drag démarrant sur une sélection de texte existante devient un
				// drag natif HTML5 → pointercancel → trait de mesure avorté
				e.preventDefault();
				window.getSelection()?.removeAllRanges();
				e.currentTarget.setPointerCapture(e.pointerId);
				dragging.current = true;
				const p = pos(e);
				setLine({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
			}}
			onPointerMove={e => {
				if (!dragging.current) return;
				const p = pos(e);
				setLine(l => (l ? { ...l, x2: p.x, y2: p.y } : l));
			}}
			onPointerUp={() => {
				dragging.current = false;
				// clic simple (trait quasi nul, < ~4 px écran) → on efface la mesure précédente
				setLine(l => (l && Math.hypot(l.x2 - l.x1, l.y2 - l.y1) * pxPerCm < 4 ? null : l));
			}}
			onPointerCancel={() => {
				dragging.current = false;
			}}
		>
			{line && (
				<svg className="dtf-measure-svg">
					<line
						x1={line.x1 * pxPerCm}
						y1={line.y1 * pxPerCm}
						x2={line.x2 * pxPerCm}
						y2={line.y2 * pxPerCm}
						stroke="#f97316"
						strokeWidth={2}
						strokeDasharray="6 3"
					/>
					<circle cx={line.x1 * pxPerCm} cy={line.y1 * pxPerCm} r={3.5} fill="#f97316" />
					<circle cx={line.x2 * pxPerCm} cy={line.y2 * pxPerCm} r={3.5} fill="#f97316" />
				</svg>
			)}
			{line && cm >= 0.05 && (
				<span
					className="dtf-measure-label"
					style={{ left: ((line.x1 + line.x2) / 2) * pxPerCm, top: ((line.y1 + line.y2) / 2) * pxPerCm }}
				>
					{cm.toFixed(1)} cm
				</span>
			)}
		</div>
	);
};

/** Graduations en cm : tick chaque cm, label tous les 5 cm. */
const Ruler: React.FC<{ lengthCm: number; pxPerCm: number; vertical?: boolean }> = ({
	lengthCm,
	pxPerCm,
	vertical,
}) => {
	const ticks: React.ReactNode[] = [];
	const n = Math.floor(lengthCm);
	for (let cm = 0; cm <= n; cm++) {
		const major = cm % 5 === 0;
		const pos = cm * pxPerCm;
		ticks.push(
			<div
				key={cm}
				className={`dtf-ruler-tick${major ? ' major' : ''}`}
				style={vertical ? { top: pos } : { left: pos }}
			>
				{major && cm > 0 && <span className="dtf-ruler-label">{cm}</span>}
			</div>,
		);
	}
	return (
		<div
			className={`dtf-ruler ${vertical ? 'dtf-ruler-v' : 'dtf-ruler-h'}`}
			style={vertical ? { height: lengthCm * pxPerCm, width: RULER } : { width: lengthCm * pxPerCm, height: RULER }}
			aria-hidden="true"
		>
			{ticks}
		</div>
	);
};

const PlancheView: React.FC<IProps> = ({
	planche,
	index,
	canDownload = false,
	onPdfServer,
	items,
	settings,
	pxPerCm,
	dark,
	measure,
	selectedId,
	hoverId,
	onSelect,
	invite,
}) => {
	const [pdfBusy, setPdfBusy] = useState(false);
	const byId = new Map(items.map(it => [it.id, it]));
	// pas de minimum PAR planche : on facture les cm réels (seul le minimum
	// de commande global s'applique) — la planche s'affiche à sa vraie hauteur
	const displayH = planche.usedCm;
	const w = settings.laizeCm * pxPerCm;
	const h = displayH * pxPerCm;

	const onPdf = async () => {
		setPdfBusy(true);
		try {
			if (onPdfServer) await onPdfServer(index);
			else await downloadPlanchePdf(planche, index, items, settings);
		} catch (_e) {
			message.error(i18next.t('dtf.err-import-generique', { nom: 'PDF' }) as string);
		} finally {
			setPdfBusy(false);
		}
	};

	return (
		<div className="dtf-planche">
			<div className="dtf-planche-head">
				<div className="dtf-planche-label">
					{i18next.t('dtf.planche')} {index + 1} — {settings.laizeCm} × {planche.usedCm.toFixed(1)} cm
					{planche.qty > 1 && (
						<span className="dtf-planche-qty dtf-planche-qty--big" title={i18next.t('dtf.planche-qty-titre', { n: planche.qty }) as string}>
							× {planche.qty} <small>{i18next.t('dtf.planche-exemplaires')}</small>
						</span>
					)}
				</div>
				{canDownload && planche.placements.length > 0 && (
					<div className="dtf-planche-dl" title={i18next.t('dtf.telecharger-test') as string}>
						<Button size="small" loading={pdfBusy} onClick={onPdf}>
							{i18next.t('dtf.telecharger-pdf')}
						</Button>
					</div>
				)}
			</div>
			<div className="dtf-planche-grid">
				<div className="dtf-ruler-corner" style={{ width: RULER, height: RULER }} />
				<Ruler lengthCm={settings.laizeCm} pxPerCm={pxPerCm} />
				<Ruler lengthCm={displayH} pxPerCm={pxPerCm} vertical />
				<div
					className={`dtf-planche-board${dark ? ' dtf-board-dark' : ''}`}
					style={{ width: w, height: h }}
					onClick={() => onSelect && onSelect(null)}
				>
					{/* Daniel 21.10.2026 : le nombre d'exemplaires est dans la pastille d'en-tête (× n EXEMPLAIRES), rien sur le canevas */}
					{planche.placements.map((p, i) => {
						const item = byId.get(p.itemId);
						if (!item) return null;
						const rot = p.rotDeg || 0;
						// p.wCm/hCm = bbox pivoté ; le logo garde ses dims réelles, centré + tourné dans le bbox
						const iw = rot ? item.widthCm : p.wCm;
						const ih = rot ? item.heightCm : p.hCm;
						const sel = selectedId === item.id;
						return (
							<div
								key={i}
								data-item={item.id}
								className={`dtf-placement-box${sel ? ' dtf-sel' : ''}${hoverId === item.id ? ' dtf-hov' : ''}`}
								style={{
									left: p.xCm * pxPerCm,
									top: p.yCm * pxPerCm,
									width: p.wCm * pxPerCm,
									height: p.hCm * pxPerCm,
								}}
								onClick={e => {
									e.stopPropagation();
									if (onSelect) onSelect(sel ? null : item.id);
								}}
							>
								<img
									src={item.src}
									alt=""
									draggable={false}
									title={`${iw} × ${ih} cm${rot ? ` (${rot}°)` : ''}`}
									className="dtf-placement"
									style={
										rot
											? {
													width: iw * pxPerCm,
													height: ih * pxPerCm,
													position: 'absolute',
													left: (p.wCm - iw) * pxPerCm * 0.5,
													top: (p.hCm - ih) * pxPerCm * 0.5,
													transform: `rotate(${rot}deg)`,
												}
											: undefined
									}
								/>
								<span className="dtf-placement-dims">
									{iw} × {ih} cm
								</span>
							</div>
						);
					})}
					{invite}
					{measure && <MeasureOverlay pxPerCm={pxPerCm} />}
				</div>
			</div>
		</div>
	);
};

export default PlancheView;
